import { Component } from 'react';

import { logger } from '@/lib/logger';

import type { ErrorInfo, ReactNode } from 'react';

export interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Renders the fallback UI for a caught render error. Receives a `reset`
   * callback that clears the captured error and re-renders `children` — so a
   * transient failure (or one the user has navigated past) can recover without
   * a full page reload.
   */
  fallback: (reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Generic React error boundary. React error boundaries MUST be class
 * components — there is no hook equivalent for `componentDidCatch`. This is the
 * one sanctioned class component in `apps/web`; the localized, themed fallback
 * it renders (`AppErrorFallback`) is a normal function component passed via the
 * `fallback` render prop, so all copy still flows through `useT()`.
 *
 * Wired once at the app root (`core/providers/Providers.tsx`) so a single bad
 * render in any page can no longer white-screen the whole dashboard.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error('[app] uncaught render error', {
      error,
      componentStack: info.componentStack,
    });
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (this.state.error !== null) {
      return this.props.fallback(this.reset);
    }
    return this.props.children;
  }
}
